import { useControllerApi, useQueryWrapper } from "@repo/baseCloud";
import { FilterItem, FilterItemPascal, KeyedArray, mapToKeyedArray, mapToKeyedArrayPascal } from "@repo/models";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import ApiRoute from "./apiRoute";

export function useRepPortalApi(controller = ApiRoute) {
	return useControllerApi(controller);
}

export { useQueryWrapper };

export function useFilterItemQueryWrapper<T extends FilterItemPascal = FilterItemPascal>(
	key: string[],
	triggers: any[],
	apiFn: () => Promise<T[]>,
	options?: any,
) {
	const callApi = async () => await apiFn().then(mapToKeyedArrayPascal);

	const queryClient = useQueryClient();
	const query = useQuery<KeyedArray<T>>({
		queryKey: [...key, ...triggers],
		queryFn: callApi,
		...options,
	});
	const loaded = !query.isFetching;
	const clearQuery = useCallback(() => queryClient.invalidateQueries({ queryKey: [...key] }), [queryClient, ...key]);

	return { data: query.data, loaded, clearQuery };
}

export function useFilterItemV2QueryWrapper<T extends FilterItem = FilterItem>(
	key: string[],
	triggers: any[],
	apiFn: () => Promise<(T | FilterItem)[]>,
	options?: any,
	valueProp: string = "value",
) {
	const callApi = async () => await apiFn().then(mapToKeyedArray);

	const queryClient = useQueryClient();
	const query = useQuery<KeyedArray<T, number>>({
		queryKey: [...key, ...triggers],
		queryFn: callApi,
		...options,
	});
	const loaded = !query.isFetching;
	const clearQuery = useCallback(() => queryClient.invalidateQueries({ queryKey: [...key] }), [queryClient, ...key]);
	return { data: mapToKeyedArray(formatData(query.data, valueProp)), loaded, clearQuery };

	function formatData(data: KeyedArray<FilterItem>, valueProp: string) {
		if (!data) return [] as FilterItem[];
		//MSC 4/8/25 Including name as I'm not sure if it's used...added value as that's definitely used
		return data.map((c) => ({ id: c.id, value: c[valueProp], name: c.name }) as FilterItem);
	}
}
